import { Menu as BaseMenu } from "@base-ui/react/menu";
import { "ui-overlay.more" as moreLabel } from "@repo/i18n";
import { Ellipsis } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { cx } from "../../lib/cx";
import { Icon } from "../controls/icon";

export interface MenuProps {
  align?: "start" | "center" | "end";
  /** `MenuItem`s and `MenuSeparator`s. */
  children: ReactNode;
  /**
   * The trigger's accessible name, e.g. "Mais opções para Supino". Defaults
   * to "Mais opções". Used by the default ellipsis trigger only.
   */
  label?: string;
  onOpenChange?: (open: boolean) => void;
  /** Optional control; a menu is ephemeral, never a URL layer (D02). */
  open?: boolean;
  side?: "top" | "bottom" | "left" | "right";
  /** Replaces the default ellipsis button; Base UI merges its props into it. */
  trigger?: ReactElement;
}

/**
 * The overflow menu, over Base UI Menu: arrow keys, Home/End, type-ahead,
 * Escape, and focus back on the trigger. Replaces A2's `CardMenu`, which had
 * `role=menu` and none of that.
 *
 * The popup carries `data-ephemeral`: T10's Android Back bridge dispatches
 * Escape while `[data-ephemeral][data-open]` exists, so Back closes the menu
 * and not the sheet under it.
 */
export function Menu({
  children,
  label,
  trigger,
  side,
  align,
  open,
  onOpenChange,
}: MenuProps) {
  return (
    <BaseMenu.Root onOpenChange={onOpenChange} open={open}>
      <BaseMenu.Trigger
        aria-label={trigger ? undefined : (label ?? moreLabel())}
        className={
          trigger
            ? undefined
            : "grid size-12 place-items-center rounded-full data-popup-open:[&>span]:bg-surface-2"
        }
        render={trigger}
      >
        {trigger ? undefined : (
          <span className="grid size-9 place-items-center rounded-full bg-canvas text-ink-2">
            <Icon icon={Ellipsis} />
          </span>
        )}
      </BaseMenu.Trigger>
      <BaseMenu.Portal>
        <BaseMenu.Positioner
          align={align ?? "end"}
          className="z-[70]"
          collisionPadding={12}
          side={side ?? "bottom"}
          sideOffset={4}
        >
          <BaseMenu.Popup
            className="a-card w-[260px] origin-(--transform-origin) p-1.5 shadow-lift outline-none transition-[opacity,transform] duration-150 data-ending-style:scale-95 data-starting-style:scale-95 data-ending-style:opacity-0 data-starting-style:opacity-0"
            data-ephemeral=""
          >
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}

export interface MenuItemProps {
  disabled?: boolean;
  icon?: ReactNode;
  label: ReactNode;
  /** A second, smaller line (A2: "Sai das sugestões para sempre"). */
  note?: ReactNode;
  onSelect: () => void;
  tone?: "default" | "danger";
}

/** A 48 px row. Selecting it closes the menu and returns focus to the trigger. */
export function MenuItem({
  label,
  note,
  icon,
  onSelect,
  tone,
  disabled,
}: MenuItemProps) {
  return (
    <BaseMenu.Item
      className={cx(
        "flex min-h-12 w-full cursor-default select-none items-center gap-3 rounded-lg px-3 py-2 text-left outline-none data-highlighted:bg-surface-2 data-disabled:opacity-40",
        tone === "danger" ? "text-danger" : "text-ink"
      )}
      disabled={disabled}
      label={typeof label === "string" ? label : undefined}
      onClick={onSelect}
    >
      {icon ? (
        <span
          className={cx(
            "shrink-0",
            tone === "danger" ? "text-danger" : "text-ink-2"
          )}
        >
          {icon}
        </span>
      ) : null}
      <span className="min-w-0">
        <span className="block font-semibold text-base leading-tight">
          {label}
        </span>
        {note ? <span className="block text-ink-3 text-xs">{note}</span> : null}
      </span>
    </BaseMenu.Item>
  );
}

export function MenuSeparator() {
  return <BaseMenu.Separator className="mx-3 my-1 h-px bg-line" />;
}
